import type { AnnotatedJsonSchema } from '@web-config-app/core';
import { generateId } from '@web-config-app/core';
import {
  getResolvedSchemaNodeAtPath,
  findSchemaPathsWhere,
  convertSchemaPathToJsonPath,
} from '@web-config-app/schema-utils';
import jp from 'jsonpath';

/**
 * Updates schema-declared reference fields when they point to regenerated IDs.
 * This keeps link fields like flow transitions in sync without rewriting
 * unrelated string content elsewhere in the payload.
 */
const replaceGeneratedIdReferences = (
  data: object,
  schema: AnnotatedJsonSchema,
  generatedIdSchemaPaths: Set<string>,
  idMap: Map<string, string>,
): void => {
  const foundReferenceSchemaPaths = findSchemaPathsWhere(
    schema,
    (schemaNode) => {
      const optionValuePath =
        schemaNode['x-entity-data-source']?.mapping?.optionValuePath;
      return (
        typeof optionValuePath === 'string' &&
        Array.from(generatedIdSchemaPaths).some((p) =>
          p.endsWith(`.${optionValuePath}`),
        )
      );
    },
  );

  foundReferenceSchemaPaths.forEach((schemaPath) => {
    const jsonPath = convertSchemaPathToJsonPath(schemaPath);
    const nodes = jp.nodes(data, jsonPath);

    nodes.forEach(({ path: nodePath, value }) => {
      if (typeof value !== 'string') {
        return;
      }

      const replacementId = idMap.get(value);
      if (!replacementId) {
        return;
      }

      jp.value(data, jp.stringify(nodePath), replacementId);
    });
  });
};

export const regenerateGeneratedIds = <T extends object>(
  data: T,
  schema: AnnotatedJsonSchema,
): T => {
  const dataCopy = JSON.parse(JSON.stringify(data)) as T;

  // Find ALL paths with x-entity-generate across all combinator branches
  const foundGeneratedIdSchemaPaths = findSchemaPathsWhere(
    schema,
    (s) => s['x-entity-generate']?.type === 'id',
  );

  // First pass: regenerate IDs and collect old→new mappings
  const idMap = new Map<string, string>();

  foundGeneratedIdSchemaPaths.forEach((schemaPath) => {
    const jsonPath = convertSchemaPathToJsonPath(schemaPath);

    // Get all data nodes that match this path
    const nodes = jp.nodes(dataCopy, jsonPath);

    nodes.forEach(({ path: nodePath }) => {
      // Convert JSONPath array to dot notation
      // nodePath is like ['$', 'items', 0, 'nodeId']
      // We need 'items.0.nodeId'
      const dataPath = nodePath.slice(1).join('.');

      // Resolve the schema node for this specific data item
      // This handles oneOf discriminators by looking at the actual data
      const resolvedSchemaNode = getResolvedSchemaNodeAtPath(
        dataPath,
        schema,
        dataCopy,
      );

      if (!resolvedSchemaNode) {
        return;
      }

      const { 'x-entity-generate': generateAnnotation } = resolvedSchemaNode;
      if (
        generateAnnotation?.type === 'id' &&
        generateAnnotation.options?.id?.regenerateOnDuplicate !== false
      ) {
        const oldId = jp.value(dataCopy, jp.stringify(nodePath));
        const newId = generateId(generateAnnotation.options?.id);
        if (typeof oldId === 'string' && oldId.length > 0) {
          idMap.set(oldId, newId);
        }
        jp.value(dataCopy, jp.stringify(nodePath), newId);
      }
    });
  });

  // Second pass: update schema-declared reference fields to the new IDs
  if (idMap.size > 0) {
    replaceGeneratedIdReferences(
      dataCopy,
      schema,
      foundGeneratedIdSchemaPaths,
      idMap,
    );
  }

  return dataCopy;
};
